import { CommonModule, DOCUMENT } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { AuthService } from "app/core/auth/auth.service";
import { Subscription, fromEvent, take } from "rxjs";

import {
  createVenueMatchmakingIdempotencyKey,
  VenueMatchmakingService,
} from "./venue-matchmaking.service";
import {
  RequestProjectionV1,
  VenueMatchmakingControl,
  VenueMatchmakingUiError,
  controlStatusMessage,
  isTerminalRequest,
  requestStateLabel,
} from "./venue-matchmaking.types";

const POLL_INTERVAL_MS = 15_000;

@Component({
  selector: "app-venue-matchmaking-trigger",
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
  ],
  templateUrl: "./venue-matchmaking-trigger.component.html",
  styleUrl: "./venue-matchmaking-trigger.styles.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueMatchmakingTriggerComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) bookingId = "";
  @Input() bookingType: string | null | undefined = "private";

  @ViewChild("triggerButton") triggerButton?: ElementRef<HTMLElement>;
  @ViewChild("statusMessage")
  set statusMessage(element: ElementRef<HTMLElement> | undefined) {
    if (!element || !this.pendingStatusFocus) return;
    this.pendingStatusFocus = false;
    element.nativeElement.focus();
  }

  control: VenueMatchmakingControl | null = null;
  loading = false;
  submitting = false;
  error: VenueMatchmakingUiError | null = null;
  successMessage = "";

  private readonly auth = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmation = inject(FuseConfirmationService);
  private readonly document = inject(DOCUMENT);
  private readonly service = inject(VenueMatchmakingService);
  private readonly subscriptions = new Subscription();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private idempotencyKey: string | null = null;
  private attemptCount = 0;
  private pendingStatusFocus = false;

  get isAdmin(): boolean {
    return ["admin", "super-admin"].includes(this.auth.getUser()?.role ?? "");
  }

  get isPublicBooking(): boolean {
    return this.bookingType?.trim().toLowerCase() === "public";
  }

  get request(): RequestProjectionV1 | null {
    return this.control?.request ?? null;
  }

  get stateLabel(): string {
    return this.request ? requestStateLabel(this.request.state) : "";
  }

  get statusCopy(): string {
    return this.control ? controlStatusMessage(this.control.status) : "";
  }

  get openPlacesLabel(): string {
    const count = Math.max(0, this.control?.openPlaces ?? 0);
    return `${count} open ${count === 1 ? "spot" : "spots"}`;
  }

  get canRetry(): boolean {
    return Boolean(
      this.error?.retryable && this.idempotencyKey && this.attemptCount < 3,
    );
  }

  ngOnInit(): void {
    if (!this.isAdmin || !this.bookingId) return;
    if (this.isPublicBooking) this.loadSummary();
    this.subscriptions.add(
      fromEvent(this.document, "visibilitychange").subscribe(() => {
        if (this.document.visibilityState === "visible") this.loadSummary();
        else this.stopPolling();
      }),
    );
    this.subscriptions.add(
      fromEvent(window, "online").subscribe(() => {
        if (this.document.visibilityState === "visible") this.loadSummary();
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const bookingChanged =
      changes["bookingId"] && !changes["bookingId"].firstChange;
    const bookingTypeChanged =
      changes["bookingType"] && !changes["bookingType"].firstChange;
    if (
      (bookingChanged || bookingTypeChanged) &&
      this.isAdmin &&
      this.bookingId
    ) {
      this.resetAction();
      if (this.isPublicBooking) this.loadSummary();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.subscriptions.unsubscribe();
  }

  confirmStart(): void {
    if (!this.control?.canTrigger || this.submitting) return;
    const dialogRef = this.confirmation.open({
      title: "Start Match Making?",
      message: `Bookletics will look for eligible players for the ${this.control.openPlaces} open spots. After it starts, closing the booking dialog will not cancel the search.`,
      icon: { show: true, name: "heroicons_outline:user-group", color: "info" },
      actions: {
        confirm: { show: true, label: "Start Match Making", color: "primary" },
        cancel: { show: true, label: "Cancel" },
      },
      dismissible: false,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result === "confirmed") this.start();
        else queueMicrotask(() => this.triggerButton?.nativeElement.focus());
      });
  }

  onToggle(enabled: boolean): void {
    if (enabled && !this.request) this.confirmStart();
  }

  retry(): void {
    if (!this.canRetry) return;
    this.start();
  }

  loadSummary(showLoading = true): void {
    if (
      !this.bookingId ||
      !this.isAdmin ||
      !this.isPublicBooking ||
      this.submitting
    )
      return;
    if (showLoading && !this.control) this.loading = true;

    this.service
      .getVenueMatchmakingControl(this.bookingId)
      .pipe(take(1))
      .subscribe({
        next: ({ item }) => {
          this.control = item;
          this.loading = false;
          this.error = null;
          this.syncPolling();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.loading = false;
          this.error = this.service.normalizeError(error);
          this.stopPolling();
          this.cdr.markForCheck();
        },
      });
  }

  private start(): void {
    if (!this.bookingId || !this.isPublicBooking || this.submitting) return;
    this.idempotencyKey ??= createVenueMatchmakingIdempotencyKey();
    this.attemptCount += 1;
    this.submitting = true;
    this.error = null;
    this.successMessage = "";
    this.cdr.markForCheck();

    this.service
      .triggerVenueMatchmaking(this.bookingId, this.idempotencyKey)
      .pipe(take(1))
      .subscribe({
        next: ({ item }) => {
          this.submitting = false;
          this.successMessage =
            "Match Making started. Bookletics will look for eligible players for the open spots.";
          this.control = this.control
            ? {
                ...this.control,
                canTrigger: false,
                status: "existing_request",
                request: item,
              }
            : null;
          this.syncPolling();
          this.pendingStatusFocus = true;
          this.cdr.markForCheck();
          this.loadSummary(false);
        },
        error: (error) => {
          this.submitting = false;
          this.error = this.service.normalizeError(error);
          if (this.error.reconcile) {
            this.loadSummary(false);
          } else if (!this.error.retryable) {
            this.idempotencyKey = null;
          }
          this.cdr.markForCheck();
        },
      });
  }

  private syncPolling(): void {
    this.stopPolling();
    if (
      !this.request ||
      isTerminalRequest(this.request.state) ||
      this.document.visibilityState !== "visible"
    ) {
      return;
    }
    this.pollTimer = setInterval(
      () => this.loadSummary(false),
      POLL_INTERVAL_MS,
    );
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private resetAction(): void {
    this.control = null;
    this.error = null;
    this.successMessage = "";
    this.idempotencyKey = null;
    this.attemptCount = 0;
    this.pendingStatusFocus = false;
    this.stopPolling();
  }
}
